/**
 * NotificationsDropdown — Bell dropdown rediseñado
 *
 * Mejoras v2:
 *   - 2 tabs: "Sin leer" (default) | "Todas"
 *   - Botón "Marcar todas como leídas" con texto visible (no solo ícono)
 *   - Botón "Limpiar leídas" para borrar definitivamente las leídas
 *   - Empty state contextual ("Sin pendientes" vs "Sin notificaciones")
 *   - Swipe-to-delete en mobile + botón delete en hover desktop
 *   - Polling cada 15s sólo cuando tab visible (visibility API)
 *   - Sin race condition entre optimistic update + poll
 */
import { useState, useEffect, useRef, useCallback } from "react";
import Glifo from "./Glifo.jsx";
import { useNavigate } from "react-router-dom";
import {
  Bell, CheckCheck, Inbox, X, Loader2, ArrowRight,
  Sparkles, AlertCircle, MessageSquare, Camera, FolderOpen, CheckCircle2,
  TrendingDown, Users, FileBarChart, Calendar, Mic, Mail, MessageCircle,
  Trash2,
} from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";
import { Tooltip } from "./ui/tooltip.jsx";
import { BandejaPanel, PildoraBandeja, FilaBandeja, TituloGrupo, EsqueletoBandeja, VacioBandeja, agruparPorDia, haceCuanto } from "./BandejaPanel.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useConfirm } from "./ConfirmDialog.jsx";
import { enlaceDeNotif, cantidadDeTareasEnLink } from "../lib/notifLink.js";

const POLL_INTERVAL = 15_000;

/* Mapeo tipo → icon + color */
const TYPE_CONFIG = {
  "tarea-asignada":      { icon: MessageSquare,  color: "text-[#0E7490]",    bg: "bg-[#0E7490]/10"    },
  "tarea-vencida":       { icon: AlertCircle,    color: "text-[#DC2626]",     bg: "bg-[#DC2626]/10"     },
  "revision-cliente":    { icon: CheckCircle2,   color: "text-[#7C3AED]",    bg: "bg-[#8B5CF6]/10"    },
  "aprobado":            { icon: CheckCircle2,   color: "text-[#059669]", bg: "bg-[#10B981]/10" },
  "cobertura":           { icon: Camera,         color: "text-[#7C3AED]",    bg: "bg-[#8B5CF6]/10"    },
  "carpetas-creadas":    { icon: FolderOpen,     color: "text-[#D97706]",   bg: "bg-[#F59E0B]/10"   },
  "reporte-listo":       { icon: FileBarChart,   color: "text-[#7C3AED]",  bg: "bg-[#8B5CF6]/10"  },
  "presupuesto-alerta":  { icon: TrendingDown,   color: "text-[#DC2626]",     bg: "bg-[#DC2626]/10"     },
  "gemini-estimacion":   { icon: Sparkles,       color: "text-[#7C3AED]",  bg: "bg-[#8B5CF6]/10"  },
  "asignacion":          { icon: Users,          color: "text-accent",      bg: "bg-accent/10"      },
  "calendar":            { icon: Calendar,       color: "text-[#0E7490]",    bg: "bg-[#0E7490]/10"    },
  "pedido-cliente":      { icon: Inbox,          color: "text-[#D97706]",   bg: "bg-[#F59E0B]/10"   },
  "mensaje-cliente":     { icon: Mail,           color: "text-[#7C3AED]",    bg: "bg-[#8B5CF6]/10"    },
  "cobertura-proxima":   { icon: Camera,         color: "text-[#7C3AED]",    bg: "bg-[#8B5CF6]/10"    },
  "para-qc":             { icon: CheckCircle2,   color: "text-[#D97706]",   bg: "bg-[#F59E0B]/10"   },
  "mencion-chat":        { icon: MessageCircle,  color: "text-[#7C3AED]",  bg: "bg-[#8B5CF6]/10"  },
  "mention":             { icon: MessageCircle,  color: "text-[#7C3AED]",  bg: "bg-[#8B5CF6]/10"  },
  "audio-transcripto":   { icon: Mic,            color: "text-[#7C3AED]",  bg: "bg-[#8B5CF6]/10"  },
  "cliente-activado":    { icon: Users,          color: "text-[#059669]", bg: "bg-[#10B981]/10" },
  "default":             { icon: Bell,           color: "text-muted-foreground", bg: "bg-muted/30"  },
};


/* Tipo de notificación → ícono, tono y categoría (los filtros de arriba). */
const CATEGORIAS = [
  { k: "tareas", l: "Tareas" }, { k: "clientes", l: "Clientes" }, { k: "coberturas", l: "Coberturas" },
  { k: "menciones", l: "Menciones" }, { k: "sistema", l: "Sistema" },
];
const TIPO_MATERIAL = {
  "tarea-asignada":     { i: "task_alt", t: "c", c: "tareas" },
  "tarea-vencida":      { i: "error", t: "r", c: "tareas" },
  "revision-cliente":   { i: "check_circle", t: "v", c: "clientes" },
  "aprobado":           { i: "check_circle", t: "g", c: "clientes" },
  "cobertura":          { i: "photo_camera", t: "v", c: "coberturas" },
  "cobertura-proxima":  { i: "photo_camera", t: "v", c: "coberturas" },
  "carpetas-creadas":   { i: "folder", t: "a", c: "sistema" },
  "reporte-listo":      { i: "bar_chart", t: "v", c: "sistema" },
  "presupuesto-alerta": { i: "trending_down", t: "r", c: "sistema" },
  "gemini-estimacion":  { i: "auto_awesome", t: "v", c: "sistema" },
  "asignacion":         { i: "groups", t: "c", c: "tareas" },
  "calendar":           { i: "calendar_month", t: "c", c: "sistema" },
  "pedido-cliente":     { i: "inbox", t: "c", c: "clientes" },
  "mensaje-cliente":    { i: "mail", t: "v", c: "clientes" },
  "para-qc":            { i: "check_circle", t: "a", c: "tareas" },
  "mencion-chat":       { i: "alternate_email", t: "v", c: "menciones" },
  "mention":            { i: "alternate_email", t: "v", c: "menciones" },
  "audio-transcripto":  { i: "mic", t: "v", c: "sistema" },
  "cliente-activado":   { i: "groups", t: "g", c: "clientes" },
  "default":            { i: "notifications", t: "c", c: "sistema" },
};

export function NotificationsDropdown() {
  const { miembro } = useAuth();
  const confirm = useConfirm();
  const userId = miembro?.id;
  const [open,        setOpen]        = useState(false);
  const [tab,         setTab]         = useState("unread");  // 'unread' | 'all'
  const [notifs,      setNotifs]      = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [pendingIds,  setPendingIds]  = useState(new Set());  // ids con PATCH en flight
  const dropdownRef      = useRef(null);  // wrapper del botón campana
  const buttonRef        = useRef(null);
  const dropdownPanelRef = useRef(null); // panel del portal (fuera del DOM del wrapper)

  /* ── Fetch notifications ────────────────────────────────────────────────── */
  // pendingIds excluido de deps — no queremos re-crear fetchNotifs cada vez que
  // cambia un pending, eso causaba re-fetches que pisaban el estado optimista.
  const pendingIdsRef = useRef(pendingIds);
  useEffect(() => { pendingIdsRef.current = pendingIds; }, [pendingIds]);

  const fetchNotifs = useCallback(async (currentTab) => {
    if (!userId) { setNotifs([]); setUnreadCount(0); return; }
    setLoading(true);
    setError(null);
    try {
      const params = { user_id: userId, limit: 50 };
      if ((currentTab || tab) === "unread") params.unread_only = "true";
      const res = await api.getNotifications(params);
      const incoming = res.notifications ?? [];
      // No pisar el optimistic state de los items en flight
      setNotifs(prev => {
        const pending = pendingIdsRef.current;
        if (pending.size === 0) return incoming;
        return incoming.map(n => {
          if (pending.has(n.id)) {
            const local = prev.find(p => p.id === n.id);
            return local || n;
          }
          return n;
        });
      });
      if (typeof res.unread_count === "number") setUnreadCount(res.unread_count);
    } catch (e) {
      setError(e.message);
      setNotifs([]);
    } finally {
      setLoading(false);
    }
  }, [userId, tab]); // ← pendingIds removido intencionalmente

  /* Initial + poll + refresh al volver a la pestaña */
  useEffect(() => {
    fetchNotifs();
    const id = setInterval(() => {
      if (!document.hidden) fetchNotifs();
    }, POLL_INTERVAL);
    const onVis = () => { if (!document.hidden) fetchNotifs(); };
    const onFocus = () => fetchNotifs();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchNotifs]);

  /* Refetch cuando cambia el tab */
  useEffect(() => {
    if (open) fetchNotifs(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /* Click outside — usa mousedown para cerrar antes del click.
     El panel está en un portal (fuera de dropdownRef), por eso necesita
     su propio ref. Si el click es dentro del botón O dentro del panel → no cerrar. */
  useEffect(() => {
    function onClickOutside(e) {
      const inButton = dropdownRef.current?.contains(e.target);
      const inPanel  = dropdownPanelRef.current?.contains(e.target);
      if (!inButton && !inPanel) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }
  }, [open]);

  /* ── Mark one as read ───────────────────────────────────────────────────── */
  async function handleRead(notif) {
    if (notif.leida) return;
    setPendingIds(p => new Set(p).add(notif.id));
    // En "Sin leer": eliminar del listado inmediatamente (mejor UX)
    // En "Todas": solo marcar como leída (dimear)
    if (tab === "unread") {
      setNotifs(prev => prev.filter(n => n.id !== notif.id));
    } else {
      setNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, leida: true } : n));
    }
    setUnreadCount(c => Math.max(0, c - 1));
    try {
      await api.markNotifRead(notif.id);
    } catch (e) {
      console.warn("[Notif] mark-read failed:", e.message);
      // Revertir: refetch para restaurar estado real
      fetchNotifs();
    } finally {
      setPendingIds(p => {
        const next = new Set(p);
        next.delete(notif.id);
        return next;
      });
    }
  }

  /* ── Mark all as read ───────────────────────────────────────────────────── */
  async function handleReadAll() {
    if (!userId || unreadCount === 0) return;
    const previous = notifs;
    setNotifs(prev => prev.map(n => ({ ...n, leida: true })));
    setUnreadCount(0);
    try {
      await api.markAllNotifsRead(userId);
      // Si estamos en tab "unread", vaciamos la lista
      if (tab === "unread") setNotifs([]);
    } catch (e) {
      console.warn("[Notif] mark-all failed:", e.message);
      setNotifs(previous);  // revertir
      fetchNotifs();
    }
  }

  /* ── Clear all read ─────────────────────────────────────────────────────── */
  async function handleClearRead() {
    if (!userId) return;
    const ok = await confirm({
      title: "¿Eliminar todas las notificaciones leídas?",
      description: "Esto no se puede deshacer.",
      variant: "destructive",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await api.clearReadNotifs(userId);
      fetchNotifs();
    } catch (e) {
      console.warn("[Notif] clear-read failed:", e.message);
    }
  }

  /* ── Delete one ─────────────────────────────────────────────────────────── */
  async function handleDelete(notif) {
    const previous = notifs;
    setNotifs(prev => prev.filter(n => n.id !== notif.id));
    if (!notif.leida) setUnreadCount(c => Math.max(0, c - 1));
    try {
      await api.deleteNotification(notif.id);
    } catch (e) {
      console.warn("[Notif] delete failed:", e.message);
      setNotifs(previous);
    }
  }

  const hasRead = notifs.some(n => n.leida);
  const navigate = useNavigate();
  const [cat, setCat] = useState("todo");
  const visibles = cat === "todo" ? notifs : notifs.filter(n => (TIPO_MATERIAL[n.tipo] || TIPO_MATERIAL.default).c === cat);

  // Abrir una notificación: la marca leída y navega a lo que corresponde (mismo comportamiento de siempre).
  function abrirNotif(n) {
    handleRead(n);
    const link = buildLink(n);
    if (!link) return;   // sin enlace: solo se marca leída y el panel sigue abierto
    setOpen(false);
    if (link.startsWith("http")) { window.open(link, "_blank", "noopener,noreferrer"); return; }
    if (window.location.hash) { try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* sin history */ } }
    try { navigate(link); setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50); }
    catch { window.location.assign(link); }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content="Notificaciones">
        <button
          ref={buttonRef}
          className="relative h-[46px] w-[46px] inline-flex items-center justify-center rounded-full bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)] hover:shadow-md text-muted-foreground hover:text-foreground transition-all"
          onClick={() => setOpen(p => !p)}
          aria-label="Notificaciones"
        >
          <Bell className="h-[22px] w-[22px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E11D48] text-[12px] font-extrabold text-white flex items-center justify-center ring-2 ring-background">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      <BandejaPanel
        abierto={open} onCerrar={() => setOpen(false)} anclaRef={buttonRef} panelRef={dropdownPanelRef}
        titulo="Notificaciones" subtitulo={unreadCount > 0 ? `${unreadCount} sin leer` : "Todo al día"}
        acciones={
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button onClick={handleReadAll} className="h-10 px-3 rounded-full text-[14px] font-extrabold text-[#0E7490] flex items-center gap-1 active:bg-muted">
                <CheckCheck className="h-4 w-4" />Marcar todas
              </button>
            )}
            {tab === "all" && hasRead && (
              <button onClick={handleClearRead} aria-label="Limpiar leídas" title="Limpiar leídas" className="h-10 w-10 rounded-full flex items-center justify-center text-muted-foreground hover:text-[#B91C1C] active:bg-muted">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        }
        pestanas={<>
          <PildoraBandeja activa={tab === "unread"} onClick={() => setTab("unread")}>Sin leer{unreadCount > 0 ? ` · ${unreadCount}` : ""}</PildoraBandeja>
          <PildoraBandeja activa={tab === "all"} onClick={() => setTab("all")}>Todas</PildoraBandeja>
          <span className="w-px h-5 bg-border flex-shrink-0 mx-1" aria-hidden="true" />
          {CATEGORIAS.map(c => (
            <PildoraBandeja key={c.k} activa={cat === c.k} onClick={() => setCat(cat === c.k ? "todo" : c.k)}>{c.l}</PildoraBandeja>
          ))}
        </>}
      >
        {loading && notifs.length === 0 ? (
          <EsqueletoBandeja />
        ) : error ? (
          <VacioBandeja icono="error" titulo="No se pudieron cargar" detalle={error} />
        ) : visibles.length === 0 ? (
          <VacioBandeja icono={tab === "unread" ? "check_circle" : "notifications"}
            titulo={tab === "unread" ? "Estás al día" : "Sin notificaciones"}
            detalle={cat !== "todo" ? "No hay nada en esta categoría." : "Cuando pasen cosas importantes aparecen acá, y también te llegan al celular."} />
        ) : agruparPorDia(visibles).map(g => (
          <div key={g.t}>
            <TituloGrupo>{g.t}</TituloGrupo>
            {g.items.map(n => {
              const cfg = TIPO_MATERIAL[n.tipo] || TIPO_MATERIAL.default;
              return (
                <FilaBandeja key={n.id} icono={cfg.i} tono={cfg.t}
                  titulo={n.titulo || "Notificación"} vista={vistaSinTitulo(n)} destino={etiquetaDestino(n)} hora={haceCuanto(n.created_at)} sinLeer={!n.leida}
                  onAbrir={() => abrirNotif(n)}
                  onLeida={() => handleRead(n)} />
              );
            })}
          </div>
        ))}
      </BandejaPanel>
    </div>
  );
}

/* ── Tab button ───────────────────────────────────────────────────────────── */
function TabButton({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors -mb-px border-b-2",
        active
          ? "text-accent border-accent bg-white dark:bg-[#111318]"
          : "text-muted-foreground border-transparent hover:text-foreground"
      )}
    >
      {label}
      {count > 0 && (
        <span className={cn(
          "text-[12px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center",
          active ? "bg-accent/20 text-accent" : "bg-muted/40 text-muted-foreground"
        )}>
          {count}
        </span>
      )}
    </button>
  );
}

/** Lo que dice el aviso sin repetir el título (casi todos arrancan con él) y en una sola línea corrida. */
function vistaSinTitulo(n) {
  let m = String(n.mensaje || "").trim();
  const t = String(n.titulo || "").trim();
  if (t && m.startsWith(t)) m = m.slice(t.length);
  return m.replace(/\s*\n+\s*/g, " · ").replace(/^[\s·]+/, "").replace(/\*/g, "").replace(/_\(\+ \d+ más\)_/g, "").trim();
}

/** A dónde lleva, dicho con palabras: antes de tocar ya se sabe qué se va a abrir. */
function etiquetaDestino(n) {
  const link = enlaceDeNotif(n);
  if (!link) return null;
  const k = cantidadDeTareasEnLink(link);
  if (k > 0) return `Ver las ${k} tareas`;
  if (link.startsWith("/tareas?focus=")) return "Abrir la tarea";
  if (link.startsWith("/coberturas-v2?cobertura=")) return "Abrir la cobertura";
  if (link.startsWith("/coberturas-v2")) return "Ver las coberturas";
  if (link.startsWith("/mi-dia/usuario/")) return "Ver su día";
  if (link === "/mi-dia") return "Ir a mi día";
  if (link.startsWith("/publicacion")) return "Abrir la bandeja de publicación";
  if (link.startsWith("/chat")) return "Abrir el chat";
  if (link.startsWith("/clientes/")) return "Abrir el cliente";
  if (link.startsWith("/pedidos-cliente")) return "Ver el pedido";
  if (link.startsWith("/carga-forecast")) return "Ver la carga del equipo";
  if (link.startsWith("/mi-perfil")) return "Ver mi perfil";
  if (link.startsWith("/tareas")) return "Ver las tareas";
  return "Abrir";
}

/* ── Link builder desde entidad ───────────────────────────────────────────── */
function buildLink(notif) {
  // Un solo criterio para toda la app (src/lib/notifLink.js): el link concreto que trae el aviso, o la tarea / cobertura a la que se refiere.
  return enlaceDeNotif(notif);
}

/* ── Notification item ────────────────────────────────────────────────────── */
function NotifItem({ notif, onRead, onDelete, onClose }) {
  const navigate = useNavigate();
  const config = TYPE_CONFIG[notif.tipo] ?? TYPE_CONFIG.default;
  const Icon   = config.icon;
  const link   = buildLink(notif);

  function handleClick(e) {
    if (e?.target?.closest("[data-stop-prop]")) return;
    onRead(notif);
    if (!link) return;  // sin link → solo marcar como leída, NO cerrar dropdown
    onClose?.();        // cerrar solo cuando vamos a navegar
    if (link.startsWith("http")) {
      window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    if (window.location.hash) {
      try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch {}
    }
    try {
      navigate(link);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
    } catch (err) {
      console.warn("[NotifClick] navigate fail:", err);
      window.location.assign(link);
    }
  }

  function handleDeleteClick(e) {
    e.stopPropagation();
    onDelete?.(notif);
  }

  function handleReadClick(e) {
    e.stopPropagation();
    if (!notif.leida) onRead(notif);
  }

  const ctaLabel = ({
    "tarea":          "Abrir tarea",
    "factura":        "Abrir factura",
    "cliente":        "Abrir cliente",
    "proyecto":       "Abrir proyecto",
    "produccion":     "Abrir producción",
    "pedido-cliente": "Abrir pedido",
  }[notif.entidad_tipo]) || "Abrir";

  return (
    <div
      onClick={handleClick}
      className={cn(
        "w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors group cursor-pointer relative",
        "hover:bg-muted/30",
        !notif.leida && "bg-accent/[0.04]"
      )}
    >
      {/* Icon */}
      <div className={cn("flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center", config.bg)}>
        <Icon className={cn("h-3.5 w-3.5", config.color)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            {notif.titulo && (
              <p className={cn(
                "text-xs leading-snug font-semibold",
                notif.leida ? "text-muted-foreground" : "text-foreground"
              )}>
                {notif.titulo}
              </p>
            )}
            {notif.mensaje && (
              <p className={cn(
                "text-[12px] leading-snug mt-0.5 line-clamp-2",
                notif.leida ? "text-muted-foreground/70" : "text-foreground/80"
              )}>
                {notif.mensaje}
              </p>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[12px] text-muted-foreground/60">
                {formatRelativeTime(notif.created_at)}
              </span>
              {link && (
                <span className={cn(
                  "text-[12px] font-semibold inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md",
                  config.color,
                  config.bg
                )}>
                  {ctaLabel}
                  <ArrowRight className="h-2.5 w-2.5"/>
                </span>
              )}
              {/* Action chips a la derecha */}
              <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!notif.leida && (
                  <button
                    data-stop-prop
                    onClick={handleReadClick}
                    title="Marcar como leída"
                    aria-label="Marcar como leída"
                    className="p-1 rounded text-muted-foreground hover:text-[#059669] hover:bg-[#ECFDF5] dark:hover:bg-[#10B981]/10"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5"/>
                  </button>
                )}
                <button
                  data-stop-prop
                  onClick={handleDeleteClick}
                  title="Eliminar"
                  aria-label="Eliminar notificación"
                  className="p-1 rounded text-muted-foreground hover:text-[#DC2626] hover:bg-[#FEF2F2] dark:hover:bg-[#DC2626]/10"
                >
                  <Trash2 className="h-3.5 w-3.5"/>
                </button>
              </div>
            </div>
          </div>
          {!notif.leida && (
            <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0 mt-1.5" />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, muted, emoji }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      {emoji ? (
        <Glifo g={emoji} className="mb-2 text-[36px] text-muted-foreground" />
      ) : (
        <Icon className={cn("h-8 w-8 mb-2", muted ? "text-[#DC2626]/40" : "text-muted-foreground/20")} />
      )}
      <p className="text-xs font-semibold text-foreground">{title}</p>
      {description && (
        <p className="text-[12px] text-muted-foreground/70 mt-1 max-w-[240px]">{description}</p>
      )}
    </div>
  );
}

function formatRelativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;

  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "hace un momento";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString("es-PY", { day: "2-digit", month: "short" });
}
